/**
 * Reverse-coding rating-scale answers: new = (min + max) − old.
 *
 * On a 1–5 scale that maps 1↔5, 2↔4 and leaves 3 alone; on 0–4 it is 4 − old. The rule
 * works for any scale whose points are evenly spaced, including a 1–10 or 0–100 slider.
 */

import { parseNumberInput } from "@/lib/parse-number";
import { isMissingToken } from "@/lib/tool-kit/research-design/matrix";

/** The reversed value, rounded off float noise (6 − 2.2 is 3.8, not 3.8000000000000003). */
export function reverseScore(value: number, min: number, max: number): number {
  return Number((min + max - value).toFixed(10));
}

/** The longest conversion table the widget lists (a 0–20 scale fits). */
export const MAX_TABLE_ROWS = 21;

/** Each whole step of the scale beside its reversed value. */
export function conversionTable(
  min: number,
  max: number
): { from: number; to: number }[] {
  if (!(Number.isFinite(min) && Number.isFinite(max)) || max <= min) {
    return [];
  }
  const rows: { from: number; to: number }[] = [];
  for (
    let value = min;
    value <= max + 1e-9 && rows.length < MAX_TABLE_ROWS;
    value += 1
  ) {
    rows.push({ from: value, to: reverseScore(value, min, max) });
  }
  return rows;
}

const COMMA_DECIMAL = /^\s*[+-]?\d*,\d+\s*$/;

/** Write a reversed value the way its source cell was written (a decimal comma stays one). */
export function formatLike(original: string, value: number): string {
  const text = String(value);
  return COMMA_DECIMAL.test(original) ? text.replace(".", ",") : text;
}

export interface ReversedTable {
  body: string[][];
  /** Cells changed. */
  reversed: number;
  /** Numbers outside min–max: left as they were, because reversing them means nothing. */
  outOfRange: number;
  /** Blank or non-numeric cells in a reversed column, left as they were. */
  skipped: number;
}

/** Reverse the chosen columns; every other cell is passed through untouched. */
export function reverseColumns(
  body: readonly (readonly string[])[],
  columns: ReadonlySet<number>,
  min: number,
  max: number
): ReversedTable {
  let reversed = 0;
  let outOfRange = 0;
  let skipped = 0;
  const out = body.map((row) =>
    row.map((cell, column) => {
      if (!columns.has(column)) {
        return cell;
      }
      const value = isMissingToken(cell) ? null : parseNumberInput(cell);
      if (value === null) {
        skipped += 1;
        return cell;
      }
      if (value < min || value > max) {
        outOfRange += 1;
        return cell;
      }
      reversed += 1;
      return formatLike(cell, reverseScore(value, min, max));
    })
  );
  return { body: out, reversed, outOfRange, skipped };
}
